import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { imageUrl } from '../lib/images';
import { fetchCollection } from '../lib/cms';
import SEO from '../components/SEO';
import SparklePattern from '../components/SparklePattern';

export function ServiceDetail() {
  const { serviceKey } = useParams();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    fetchCollection('services').then((servicesData) => {
      setAllServices(servicesData || []);

      const foundService = (servicesData || []).find(
        (s) => s.service_key === serviceKey || s.slug?._ === serviceKey || s.slug === serviceKey
      );
      setService(foundService || null);

      setLoading(false);
    });
  }, [serviceKey]);

  if (loading) {
    return (
      <div className="py-24 text-center">
        <div className="inline-block w-8 h-8 border-4 border-sky-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-sm text-slate-500">Henter ydelsesinformation...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">
          Ydelsen blev ikke fundet
        </h1>
        <p className="text-slate-600 mb-6">
          Den ønskede ydelse findes ikke i systemet.
        </p>
        <Link
          to="/service-priser"
          className="inline-flex items-center px-6 py-2.5 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors text-sm"
        >
          <span>Se alle ydelser & priser</span>
        </Link>
      </div>
    );
  }

  const primaryImage = Array.isArray(service.billede) ? service.billede[0] : service.billede;

  return (
    <>
      <SEO
        title={`${service.title} - MA Hvidevareservice`}
        description={service.short_desc || `${service.title} hos MA Hvidevareservice i Brønshøj og Storkøbenhavn.`}
        ogImage={primaryImage}
      />

      {/* Breadcrumb / Top bar */}
      <div className="hidden md:block bg-slate-100 border-b border-slate-200/80 py-3 text-xs text-slate-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-2">
          <Link to="/" className="hover:text-sky-600 transition-colors">Forside</Link>
          <span className="text-slate-400">/</span>
          <Link to="/service-priser" className="hover:text-sky-600 transition-colors">Ydelser & Priser</Link>
          <span className="text-slate-400">/</span>
          <span className="text-slate-900 font-semibold truncate">{service.title}</span>
        </div>
      </div>

      {/* Hero Section of Service */}
      <section className="relative bg-white border-b border-slate-200/80 py-12 lg:py-16 overflow-hidden isolate">
        <SparklePattern variant="blue" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full">
            <div className="w-full space-y-5">
              {/* Clear H1 per requirement */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#53b7ff] tracking-tight leading-tight">
                {service.title}
              </h1>

              {/* Short intro per requirement */}
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
                {service.short_desc}
              </p>

              {/* Price & info strip */}
              <div className="flex flex-col gap-3 text-sm py-1">
                <div>
                  <span className="text-xs text-slate-500 font-medium block">Standard servicebesøg</span>
                  <span className="text-lg font-extrabold text-slate-900">{service.price_tag || '995 kr. inkl. moms'}</span>
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <p className="font-semibold text-slate-800">✓ Inkluderer udkørsel og fejlfinding</p>
                  <p>✓ 1–2 års garanti på arbejde og nye dele</p>
                </div>
              </div>

              {/* CTA row per requirement */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm rounded-lg transition-colors shadow-sm"
                >
                  <span>Bestil servicebesøg</span>
                </Link>

                <a
                  href="tel:69306445"
                  className="inline-flex items-center justify-center px-6 py-3.5 bg-white hover:bg-slate-50 text-slate-900 border border-slate-300 font-bold text-sm rounded-lg transition-colors whitespace-nowrap"
                  aria-label="Ring til 69 30 64 45"
                >
                  <span className="whitespace-nowrap">69 30 64 45</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Service Content */}
      <section className="py-14 sm:py-18 bg-slate-50 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Left Content column */}
            <div className="lg:col-span-8 space-y-6">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                Om vores reparation og service
              </h2>

              {service.content ? (
                <div
                  className="prose prose-slate max-w-none text-sm sm:text-base text-slate-600 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: service.content }}
                />
              ) : (
                <p className="text-sm text-slate-600 leading-relaxed">
                  Hos MA Hvidevareservice udfører vi grundig fejlfinding og reparation. Vi har 10 års erfaring på alle kendte mærker, og vi kører først videre, når maskinen fungerer 100 % som den skal.
                </p>
              )}

              {/* Service Details Box */}
              <div className="mt-8 pt-6 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 text-xs text-slate-700">
                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-file-invoice text-sky-600 text-2xl mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <strong className="block text-base font-bold text-slate-900 mb-0.5">Altid prisoverslag</strong>
                    <span>Jeg laver ALTID prisoverslag inden reparationen påbegyndes. Ingen ubehagelige overraskelser.</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-shield text-sky-600 text-2xl mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <strong className="block text-base font-bold text-slate-900 mb-0.5">1–2 års garanti</strong>
                    <span>Garantien dækker både udført arbejde og nye reservedele.</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-truck text-sky-600 text-2xl mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <strong className="block text-base font-bold text-slate-900 mb-0.5">Hurtig udrykning</strong>
                    <span>Ofte hjemmebesøg inden for 1 dag. Du kontaktes 20 minutter før ankomst.</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <i className="fa-solid fa-leaf text-sky-600 text-2xl mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <strong className="block text-base font-bold text-slate-900 mb-0.5">Grøn fornuft</strong>
                    <span>Genbrug og reparation sparer både kloden for CO₂ og din pengepung for en ny dyr maskine.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar: Direct Booking */}
            <div className="lg:col-span-4 space-y-6">
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default ServiceDetail;
